import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ProgressCard from "@/components_v2/Card/ProgressCard";
import { SectionCardProps } from "@/features/survey/summary/types";
import { ProgressStatus } from "@/components_v2/Card/types";

const SectionCard = ({ section, isSample }: SectionCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sectionActionButtonLabel = useMemo(() => {
    if (isSample) {
      return t("survey.labels.view");
    }
    return section.answeredRequiredQuestionsCount === 0
      ? t("survey.labels.start")
      : t("survey.labels.continue");
  }, [section, isSample]);

  const sectionStatus = useMemo((): ProgressStatus | undefined => {
    if (
      section.answeredRequiredQuestionsCount === 0 &&
      section.answeredOptionalQuestionsCount === 0
    )
      return;

    const requiredQuestionsLeft =
      section.requiredQuestionsCount - section.answeredRequiredQuestionsCount;
    const optionalQuestionsLeft =
      section.optionalQuestionsCount - section.answeredOptionalQuestionsCount;

    if (requiredQuestionsLeft > 0) {
      return "required-missing";
    }

    if (optionalQuestionsLeft > 0) {
      return "optional-missing";
    }

    return "completed";
  }, [section]);

  const progressBarLabel = useMemo(() => {
    return `${section.answeredRequiredQuestionsCount + section.answeredOptionalQuestionsCount}/${section.requiredQuestionsCount + section.optionalQuestionsCount} ${t("survey.labels.questionsAnswered")}`;
  }, [section]);

  return (
    <div>
      <ProgressCard
        key={section.id}
        title={section.title}
        status={sectionStatus}
        color={section.color}
        iconSrc={section.icon}
        label={progressBarLabel}
        value={
          section.answeredRequiredQuestionsCount +
          section.answeredOptionalQuestionsCount
        }
        total={section.requiredQuestionsCount + section.optionalQuestionsCount}
        buttonLabel={sectionActionButtonLabel}
        onCardClick={() =>
          navigate(
            `/reports/${section.survey}/section/${section.id}${isSample ? "/sample" : ""}`,
          )
        }
      />
    </div>
  );
};

export default SectionCard;
